import { useState } from "react";
import { Quote, ChevronDown } from "lucide-react";

const PORTRAIT = "https://customer-assets.emergentagent.com/job_point-ov-view/artifacts/ft8roqrq_IMG_6319.jpeg";

const ENGAGEMENT_BRANDS = ["Audemars Piguet", "Lucid Motors", "Moooi", "Rolex", "Puri Lighting"];
const PARTNERSHIP_BRANDS = ["Ferrari", "Roc Nation", "Royal Caribbean", "Norwegian Cruise Line", "Blade Helicopters", "LVMH"];

const BIO_PARAGRAPHS = [
  "Khrystopher Bargaineer is a high-level business development strategist, consultant, and operations leader known for helping companies break into difficult markets, build influential partnerships, and scale with precision. With more than a decade of experience across luxury brands, international sourcing, enterprise sales, hospitality, construction-adjacent industries, and strategic operations, he has built a reputation as the person organizations call when they need growth, positioning, execution, and results.",
  "Throughout his career, Khrystopher has operated at the intersection of business development, strategic partnerships, operational leadership, and high-value deal execution. His background includes managing multimillion-dollar contract negotiations, cultivating relationships with executive stakeholders, developers, architects, procurement teams, hospitality groups, investors, and ultra-high-net-worth clientele across domestic and international markets.",
  "His professional experience spans multiple elite industries and globally recognized brands, including leadership and consulting roles connected to Audemars Piguet, Lucid Motors, Moooi, Rolex, and Puri Lighting. He has also developed strategic relationships and commercial partnerships involving organizations and brands such as Ferrari, Roc Nation, Royal Caribbean International, Norwegian Cruise Line, Blade Helicopters, and LVMH.",
  "What separates Khrystopher from traditional consultants is the depth and diversity of his real-world operational experience. He has successfully led projects involving enterprise revenue growth, market penetration strategy, international procurement, vendor sourcing, hospitality partnerships, luxury sales operations, infrastructure-oriented development, and cross-functional team leadership. His ability to navigate high-pressure environments while aligning multiple stakeholders toward a shared objective has made him a trusted advisor across both private-sector and community-impact initiatives.",
  "As a consultant, Khrystopher focuses on helping businesses and organizations solve complex problems, identify strategic opportunities, improve operational structure, strengthen partnerships, and position themselves for sustainable long-term growth. Whether advising executives, scaling emerging ventures, facilitating partnerships, or helping organizations refine their vision and execution strategy, his approach is rooted in communication, accountability, adaptability, and measurable outcomes.",
  "His experience ranges from overseeing six-figure procurement engagements to structuring multimillion-dollar development and specification contracts. He has coordinated initiatives involving sales, logistics, operations, procurement, design, construction, and executive leadership teams, making him uniquely equipped to consult on projects that require both strategic vision and operational execution.",
  "Fluent in English, Hebrew, and Spanish, Khrystopher brings a global mindset to business strategy, relationship management, and organizational growth. Clients value his ability to connect with people from different industries and backgrounds while maintaining a clear focus on execution, results, and long-term impact.",
  "At Point Ov View Consulting, Khrystopher serves as Head Consultant, leading initiatives centered around strategic growth, business development, operational consulting, partnership strategy, market expansion, and organizational transformation. His multidisciplinary background, relationship-driven leadership style, and proven track record of execution make him a trusted resource for businesses, entrepreneurs, organizations, and leadership teams looking to move from ideas to meaningful results.",
];

export default function Consultant() {
  const [open, setOpen] = useState(false);

  return (
    <section id="consultant" className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="consultant-section">
      <div className="grid grid-cols-12 gap-8 items-start">
        {/* Portrait */}
        <div className="col-span-12 lg:col-span-6 relative">
          <div className="relative aspect-[4/5] overflow-hidden glass rounded-sm">
            <img src={PORTRAIT} alt="Khrystopher Bargaineer, Head Consultant" className="w-full h-full object-cover" data-testid="consultant-portrait" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-transparent to-transparent" />
            <div className="absolute left-6 bottom-6">
              <p className="overline">Head Consultant</p>
              <p className="font-display text-xl mt-1">Khrystopher Bargaineer</p>
            </div>
          </div>
          <div className="absolute -bottom-6 -right-6 glass-strong p-5 rounded-sm hidden md:flex flex-col items-start max-w-xs">
            <Quote className="w-5 h-5 text-[var(--pov-coral)] mb-3" />
            <p className="text-sm text-white/80 leading-relaxed italic">
              The person organizations call when they need growth, positioning, execution, and results.
            </p>
          </div>
        </div>

        {/* Editorial intro */}
        <div className="col-span-12 lg:col-span-6">
          <p className="overline">Head Consultant · Point Ov View Consulting</p>
          <h2 className="font-display font-light text-4xl lg:text-6xl tracking-tighter mt-4 leading-[0.95]">
            Khrystopher <br /> <span className="italic text-[var(--pov-cyan)]">Bargaineer.</span>
          </h2>
          <p className="mt-6 text-white/75 leading-relaxed">
            A high-level business development strategist, consultant, and operations leader known for
            helping companies <span className="text-white">break into difficult markets</span>, build
            influential partnerships, and scale with precision.
          </p>
          <p className="mt-4 text-white/60 leading-relaxed">
            More than a decade across luxury brands, international sourcing, enterprise sales,
            hospitality, construction-adjacent industries, and strategic operations — managing
            multimillion-dollar negotiations and ultra-high-net-worth relationships across domestic
            and international markets.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-4">
            {[
              ["Title", "Head Consultant, Point Ov View"],
              ["Tenure", "10+ years of practice"],
              ["Languages", "English · Hebrew · Spanish"],
              ["Deal Range", "6-figure → 8-figure contracts"],
            ].map(([k, v]) => (
              <div key={k} className="glass rounded-sm p-5" data-testid={`consultant-fact-${k.toLowerCase().replace(/ /g, "-")}`}>
                <p className="overline mb-2">{k}</p>
                <p className="text-sm text-white/80">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Marquee brands */}
      <div className="mt-20 grid grid-cols-12 gap-6" data-testid="brands-block">
        <div className="col-span-12 lg:col-span-6 glass rounded-sm p-8" data-testid="engagement-brands">
          <p className="overline">Leadership & Consulting Engagements</p>
          <p className="text-white/55 text-sm mt-2">
            Roles connected to globally recognized luxury, mobility, design, and education brands.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {ENGAGEMENT_BRANDS.map((b) => (
              <span
                key={b}
                className="px-3 py-1.5 text-xs tracking-wide uppercase rounded-sm border border-[var(--pov-cyan)]/30 text-white/85 bg-[var(--pov-cyan)]/5"
                data-testid={`brand-engagement-${b.toLowerCase().replace(/ /g, "-")}`}
              >
                {b}
              </span>
            ))}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-6 glass rounded-sm p-8" data-testid="partnership-brands">
          <p className="overline">Strategic Relationships & Partnerships</p>
          <p className="text-white/55 text-sm mt-2">
            Commercial partnerships across mobility, hospitality, entertainment, and luxury conglomerates.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {PARTNERSHIP_BRANDS.map((b) => (
              <span
                key={b}
                className="px-3 py-1.5 text-xs tracking-wide uppercase rounded-sm border border-[var(--pov-coral)]/30 text-white/85 bg-[var(--pov-coral)]/5"
                data-testid={`brand-partnership-${b.toLowerCase().replace(/ /g, "-")}`}
              >
                {b}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Full biography — expandable */}
      <div className="mt-12" data-testid="bio-block">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          data-testid="bio-toggle"
          className="w-full glass rounded-sm p-6 flex items-center justify-between hover:border-white/25 transition-colors group"
        >
          <div className="text-left">
            <p className="overline">{open ? "Hide" : "Read"} the Full Biography</p>
            <p className="font-display text-xl mt-1 text-white/85 group-hover:text-white transition-colors">
              The full record — career, methodology, and what separates the practice.
            </p>
          </div>
          <ChevronDown
            className={`w-6 h-6 text-[var(--pov-coral)] transition-transform duration-500 ${open ? "rotate-180" : ""}`}
          />
        </button>

        <div
          className={`grid transition-all duration-700 ease-out ${open ? "grid-rows-[1fr] opacity-100 mt-4" : "grid-rows-[0fr] opacity-0"}`}
          data-testid="bio-content"
        >
          <div className="overflow-hidden">
            <div className="glass rounded-sm p-8 md:p-12">
              <div className="max-w-3xl space-y-5 text-white/75 leading-relaxed">
                {BIO_PARAGRAPHS.map((p, i) => (
                  <p key={i} className={i === 0 ? "text-white/85 text-lg" : ""}>
                    {p}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
